import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { observer } from 'mobx-react';
import { Tooltip } from '@mui/material';
import { TbWorldShare } from 'react-icons/tb';

import { ChatIcon, ClaimsIcon, PinIcon } from '../../icons';
import { chatService } from '../../../services/chatService';
import { websocketService } from '../../../services/websocketService';
import { IChannel } from '../../../types/chat';
import { getChannelConfigForChat } from '../../../config/channels';
import ChatThumbnailMenu from '../ChatThumbnailMenu';
import ChannelTitleTooltip from '../ChannelTitleTooltip';
import { useDrag } from '../../../contexts/DragContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useChatStore } from '../../../hooks/useChatStore';
import ChatThumbnailWrapper from './ChatThumbnailWrapper';
import { formatInstant } from '../../helpers/dataHelper';
import useNotifier from '../../../hooks/useNotifier';

interface ChatThumbnailProps {
    chat: IChannel;
    onChatDeleted: (chatName: string) => void;
    onChatPinned: (chatName: string, isPinned: boolean) => void;
    onChatSelected: (chatId: string, claimId: string | undefined) => void;
    hasNotification?: boolean;
    isDragging?: boolean;
    disableMenu?: boolean;
    labelHtml?: string;
}

const ChatThumbnail: React.FC<ChatThumbnailProps> = observer(({
    chat,
    isDragging: isDraggingProp,
    onChatDeleted,
    onChatPinned,
    onChatSelected,
    hasNotification = false,
    disableMenu,
    labelHtml,
}) => {
    const notifier = useNotifier();
    const chatStore = useChatStore();
    const location = useLocation();
    const navigate = useNavigate();
    const titleRef = useRef<HTMLDivElement>(null);
    const { isDragging } = useDrag();
    const { user } = useAuth();
    const isOwnChat = !chat.user_email || chat.user_email === user?.email;
    
    // Track current path - updates on both React Router navigation AND manual replaceState
    const [currentPath, setCurrentPath] = useState(window.location.pathname);
    
    // Update currentPath when React Router location changes
    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location.pathname]);
    
    // Listen for manual URL changes (from window.history.replaceState elsewhere in the app)
    useEffect(() => {
        const handleUrlChange = (event: CustomEvent<{ chatId: string }>) => {
            setCurrentPath(window.location.pathname);
        };
        
        window.addEventListener('claimChatCreated', handleUrlChange as EventListener);
        return () => {
            window.removeEventListener('claimChatCreated', handleUrlChange as EventListener);
        };
    }, []);
    
    const [showTooltip, setShowTooltip] = useState(false);
    const [isPinning, setIsPinning] = useState(false);
    const [isCloning, setIsCloning] = useState(false);

    const filesCount = chat?.files_count || 0;

    useEffect(() => {
        if (isDragging || !!isDraggingProp && showTooltip) {
            setShowTooltip(false);
        }
    }, [isDragging, isDraggingProp, showTooltip]);

    // Get channel configuration and tag color
    const channelConfig = getChannelConfigForChat(chat);
    const tagLabel = channelConfig?.title || 'Chat';
    const titleLine = chat.label || tagLabel;
    const titleRefNativeTitle =
        chat.created_at != null && String(chat.created_at).trim() !== ''
            ? `${titleLine}\nChat started: ${formatInstant(chat.created_at)}`
            : titleLine;

    const handleShareChat = () => {
        chatStore.setChatToShare(chat);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await chatService.deleteChat(chat.name);
            if (response.success) {
                // Immediately remove from local state for instant UI update
                onChatDeleted(chat.name);
            } else {
                notifier.showError(response?.error || 'Failed to delete chat. Please try again.');
            }
        } catch (error) {
            notifier.showError(error instanceof Error ? error.message : 'An error occurred while deleting the chat. Please try again.');
        }
    };

    const handleDeleteClick = (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();

            // Skip confirmation if Shift key is held down
            if (e.shiftKey) {
                handleConfirmDelete();
                return;
            }
        }
        chatStore.setShowDeletePopup(chat);
    };

    const handleTogglePin = async (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsPinning(true);

        try {
            const response = await chatService.toggleChatPin(chat.name, !chat.pinned);
            if (response.success) {
                // Immediately update local state for instant UI update
                onChatPinned(chat.name, !chat.pinned);
            } else {
                notifier.showError(response?.error || 'Failed to update chat pin status. Please try again.');
            }
        } catch (error) {
            notifier.showError(error instanceof Error ? error.message : 'An error occurred while updating the chat pin status. Please try again.');
        } finally {
            setIsPinning(false);
        }
    };

    const handleRename = () => {
        chatStore.setShowRenamePopup(chat);
    };

    const handleCloseClick = () => {
        chatStore.setShowClosePopup(chat);
    };

    const handleClone = async () => {
        setIsCloning(true);
        try {
            // websocketService.cloneChat emits CHANNEL_ADD so the clone is
            // prepended to the sidebar client-side (refreshChannels() is a
            // no-op when the filter is unchanged, so it can't be relied on).
            const newChat = await websocketService.cloneChat(chat.name);
            const newChatId = newChat?.id;
            if (newChatId) {
                // Mirror a real thumbnail click: run the selection side-effects
                // AND navigate the URL (normally done by the NavLink). Without
                // the navigate the address bar stays on the old chat, so a
                // refresh reloads the old chat and highlighting goes inconsistent.
                onChatSelected(String(newChatId), newChat?.claim_number);
                navigate(`/channels/${newChatId}`);
            } else {
                notifier.showError('Failed to clone chat. Please try again.');
            }
        } catch (error) {
            notifier.showError(error instanceof Error ? error.message : 'An error occurred while cloning the chat. Please try again.');
        } finally {
            setIsCloning(false);
        }
    };

    // --- Click Handlers ---
    const handleChatClick = () => {
        // Always clear notification when chat is clicked, regardless of claim_number
        onChatSelected(chat.name, chat.claim_number);
    };

    const handleMenuStopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };
    
    // Check if this chat is currently active
    const chatPath = `/channels/${chat.name}`;
    const isActive = currentPath === chatPath || chat.id === chatStore.activeChatId;

    return (
        <>
            <ChatThumbnailWrapper
                chat={chat}
                hasNotification={hasNotification}
                isDragging={isDragging || !!isDraggingProp}
                handleChatClick={(e: React.MouseEvent) => {
                    // Disable onClick action and redirect if already active or is claim chat
                    if (isActive || isDragging || isDraggingProp) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                    }
                    handleChatClick();
                }}
            >
                <div className="flex items-center self-stretch w-full">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {chat.claim_number ? <ClaimsIcon /> : <ChatIcon />}
                        <div className="flex-1 overflow-hidden min-w-0">
                            <div className="flex items-center gap-1 min-w-0">
                                {labelHtml ? (
                                    <div
                                        ref={titleRef}
                                        className="text-chat-text-primary font-manrope text-md font-semibold leading-5 truncate min-w-0"
                                        onMouseEnter={() => setShowTooltip(true)}
                                        onMouseLeave={() => setShowTooltip(false)}
                                        dangerouslySetInnerHTML={{ __html: labelHtml }}
                                    />
                                ) : (
                                    <div
                                        ref={titleRef}
                                        className="text-chat-text-primary font-manrope text-md font-semibold leading-5 truncate min-w-0"
                                        onMouseEnter={() => setShowTooltip(true)}
                                        onMouseLeave={() => setShowTooltip(false)}
                                    >
                                        {titleLine}
                                    </div>
                                )}
                                {filesCount > 0 && (
                                    <span
                                        className="shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-[#E0E3E9] text-chat-text-primary font-manrope text-xs font-semibold leading-none tabular-nums mr-1"
                                        title={`${filesCount} file${filesCount === 1 ? '' : 's'}`}
                                    >
                                        {filesCount}
                                    </span>
                                )}
                            </div>
                            {!isOwnChat && chat.user_name && (
                                <div className="text-xs text-gray-500 truncate leading-4">
                                    {chat.user_name}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 relative">
                        {chat.pinned && (
                            <PinIcon className="text-hex-gray-400 shrink-0 w-4 h-4" />
                        )}

                        {chat.shared && isOwnChat ? (
                            <Tooltip arrow title="Shared with all users" placement="top">
                                <span className="shrink-0">
                                    <TbWorldShare size={16} className="text-hex-gray-400" />
                                </span>
                            </Tooltip>
                        ): null}

                        {/* Menu button positioned 1px to the right of the tag */}
                        {isOwnChat && !disableMenu ? (
                            <div
                                className={`absolute left-full ml-0.5 top-1/2 transform -translate-y-1/2 z-9999 opacity-0 ${isDragging || !!isDraggingProp ? '' : 'group-hover:opacity-100'} transition-opacity duration-200`}
                                onClick={handleMenuStopPropagation}
                            >
                                <ChatThumbnailMenu
                                    chat={chat}
                                    isPinned={chat.pinned}
                                    isPinning={isPinning}
                                    isDeleting={false}
                                    isRenaming={false}
                                    isClosing={false}
                                    isCloning={isCloning}
                                    canRename={isOwnChat}
                                    onTogglePin={handleTogglePin}
                                    onDeleteClick={handleDeleteClick}
                                    onCloseClick={handleCloseClick}
                                    onCloneClick={handleClone}
                                    onRename={handleRename}
                                    onShareChat={handleShareChat}
                                />
                            </div>
                        ): null}
                    </div>
                </div>
            </ChatThumbnailWrapper>

            {/* Channel Title Tooltip */}
            <ChannelTitleTooltip
                title={titleRefNativeTitle}
                isVisible={showTooltip}
                triggerRef={titleRef}
            />
        </>
    );
});

export default ChatThumbnail;

